import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

export const LEGAL_KIND: Record<string, string> = {
  ltda: "Sociedade limitada", slu: "Sociedade limitada unipessoal", sa: "Sociedade anônima", associacao: "Associação", fundacao: "Fundação", mei: "MEI", outra: "Outra",
};
export const PARTNER_ROLE: Record<string, string> = {
  socio: "Sócio", socio_administrador: "Sócio-administrador", socio_cotista: "Sócio cotista", associado_fundador: "Associado fundador", associado: "Associado",
  presidente: "Presidente", vice_presidente: "Vice-presidente", diretor: "Diretor", tesoureiro: "Tesoureiro", secretario: "Secretário", conselheiro_fiscal: "Conselheiro fiscal",
};
export const MEETING_KIND: Record<string, string> = {
  reuniao_socios: "Reunião de sócios", assembleia_ordinaria: "Assembleia ordinária", assembleia_extraordinaria: "Assembleia extraordinária",
  reuniao_diretoria: "Reunião da diretoria", conselho_fiscal: "Conselho fiscal",
};
export const MEETING_STATUS: StatusMap = {
  agendada: { label: "agendada", tone: "info" },
  realizada: { label: "realizada", tone: "warn" },
  ata_aprovada: { label: "ata aprovada", tone: "ok" },
  cancelada: { label: "cancelada", tone: "neutral" },
};
export const CHANGE_KIND: Record<string, string> = {
  constituicao: "Constituição", cessao: "Cessão de quotas", aumento_capital: "Aumento de capital", reducao_capital: "Redução de capital",
  entrada_socio: "Entrada de sócio", saida_socio: "Saída de sócio", ajuste: "Ajuste",
};
export const QUORUM: Record<string, string> = {
  maioria_presentes: "Maioria dos presentes", maioria_absoluta: "Maioria absoluta (mais de 50%)", dois_tercos: "Dois terços", tres_quartos: "Três quartos", unanimidade: "Unanimidade",
};
export const RESOLUTION_STATUS: StatusMap = {
  em_votacao: { label: "em votação", tone: "info" },
  aprovada: { label: "aprovada", tone: "ok" },
  rejeitada: { label: "rejeitada", tone: "crit" },
};
export const CHOICE: Record<string, string> = { favor: "a favor", contra: "contra", abstencao: "abstenção" };
export const OBLIGATION_KIND: Record<string, string> = {
  aprovacao_contas: "Aprovação de contas", registro_ata: "Registro de ata", alteracao_contratual: "Alteração contratual", renovacao_procuracao: "Renovação de procuração",
  declaracao_fiscal: "Declaração fiscal", certidao: "Certidão", assembleia: "Assembleia", mandato: "Mandato", outra: "Outra",
};
export const RECURRENCE: Record<string, string> = { unica: "Única", mensal: "Mensal", trimestral: "Trimestral", anual: "Anual" };
export const OBLIGATION_STATE: StatusMap = {
  em_dia: { label: "em dia", tone: "neutral" },
  em_alerta: { label: "em alerta", tone: "warn" },
  vencida: { label: "vencida", tone: "crit" },
  cumprida: { label: "cumprida", tone: "ok" },
  dispensada: { label: "dispensada", tone: "neutral" },
};

/** Pontos-base (10000 = 100%) em texto pt-BR com duas casas. */
export function bps(v: number | null | undefined): string {
  if (v === null || v === undefined) return "-";
  return `${(v / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

export interface Entity {
  id: string;
  name: string;
  trade_name: string | null;
  legal_kind: string;
  has_capital: boolean;
  cnpj: string | null;
  issuer_id: string | null;
  registry: string | null;
  registry_number: string | null;
  founded_on: string | null;
  status: string;
  notes: string | null;
}
export interface Partner {
  id: string;
  entity_id: string;
  person_id: string | null;
  name: string;
  kind: string;
  doc: string | null;
  role: string;
  is_administrator: boolean;
  has_vote: boolean;
  joined_on: string | null;
  left_on: string | null;
  status: string;
}
export interface CapRow {
  entity_id: string;
  change_id: string;
  seq: number;
  kind: string;
  effective_on: string;
  entity_capital_cents: number;
  total_quotas: number;
  partner_id: string;
  partner_name: string;
  role: string;
  quotas: number;
  basis_points: number;
  capital_cents: number;
}
export interface CapChange {
  id: string;
  seq: number;
  kind: string;
  effective_on: string;
  description: string;
  capital_cents: number;
  total_quotas: number;
  meeting_id: string | null;
  registry_protocol: string | null;
  created_at: string;
  cap_table_lines: Array<{ partner_id: string; quotas: number; basis_points: number; capital_cents: number }>;
}
export interface Meeting {
  id: string;
  entity_id: string;
  kind: string;
  title: string;
  scheduled_at: string;
  held_at: string | null;
  location: string | null;
  call_notice: string | null;
  status: string;
  minutes_text: string | null;
  minutes_doc_code: string | null;
  registry_protocol: string | null;
  canceled_reason: string | null;
}
export interface Resolution {
  id: string;
  meeting_id: string;
  number: number;
  subject: string;
  body: string | null;
  quorum_rule: string;
  status: string;
  favor_bps: number | null;
  contra_bps: number | null;
  abstencao_bps: number | null;
  decided_at: string | null;
}
export interface ProxyView {
  id: string;
  entity_id: string;
  entity_name: string;
  grantor_partner_id: string | null;
  grantor_name: string;
  grantee_name: string;
  powers: string;
  scope: string;
  for_meetings: boolean;
  valid_from: string;
  valid_until: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  document_code: string | null;
  is_valid: boolean;
  days_to_expire: number | null;
}
export interface ObligationView {
  id: string;
  entity_id: string;
  entity_name: string;
  title: string;
  kind: string;
  legal_ref: string | null;
  due_on: string;
  recurrence: string;
  alert_days: number;
  responsible_person_id: string | null;
  responsible_name: string | null;
  status: string;
  done_on: string | null;
  evidence: string | null;
  days_to_due: number | null;
  state: string;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "governance.upsert_entity": {
      args: { p_org: U; p_name: string; p_legal_kind: string; p_has_capital: boolean; p_cnpj?: string | null; p_issuer?: U | null; p_trade_name?: string | null; p_registry?: string | null; p_registry_number?: string | null; p_founded_on?: string | null; p_notes?: string | null; p_id?: U | null };
      returns: U;
    };
    "governance.upsert_partner": {
      args: { p_entity: U; p_name: string; p_role: string; p_doc?: string | null; p_person?: U | null; p_kind?: string; p_is_administrator?: boolean; p_has_vote?: boolean; p_joined_on?: string | null; p_id?: U | null };
      returns: U;
    };
    "governance.retire_partner": { args: { p_partner: U; p_left_on?: string | null }; returns: null };
    "governance.record_cap_table_change": {
      args: { p_entity: U; p_kind: string; p_effective_on: string; p_description: string; p_lines: Array<{ partner_id: U; quotas: number; basis_points?: number }>; p_capital_cents: number; p_meeting?: U | null; p_registry_protocol?: string | null; p_idempotency_key?: string | null };
      returns: Record<string, unknown>;
    };
    "governance.grant_proxy": {
      args: { p_entity: U; p_grantor_name: string; p_grantee_name: string; p_powers: string; p_scope: string; p_valid_from: string; p_valid_until?: string | null; p_grantor_partner?: U | null; p_grantee_person?: U | null; p_grantee_doc?: string | null; p_for_meetings?: boolean; p_document_code?: string | null };
      returns: U;
    };
    "governance.revoke_proxy": { args: { p_proxy: U; p_reason: string }; returns: null };
    "governance.schedule_meeting": { args: { p_entity: U; p_kind: string; p_title: string; p_scheduled_at: string; p_location?: string | null; p_call_notice?: string | null }; returns: U };
    "governance.record_attendance": { args: { p_meeting: U; p_partner: U; p_present: boolean; p_proxy?: U | null }; returns: U };
    "governance.add_resolution": { args: { p_meeting: U; p_subject: string; p_quorum_rule: string; p_body?: string | null }; returns: U };
    "governance.cast_vote": { args: { p_resolution: U; p_partner: U; p_choice: string; p_proxy?: U | null }; returns: U };
    "governance.close_resolution": { args: { p_resolution: U }; returns: { status: string; favor_bps: number; contra_bps: number; abstencao_bps: number } };
    "governance.hold_meeting": { args: { p_meeting: U; p_held_at?: string | null; p_minutes_text?: string | null }; returns: null };
    "governance.approve_minutes": { args: { p_meeting: U; p_minutes_text?: string | null; p_minutes_doc_code?: string | null; p_registry_protocol?: string | null }; returns: Record<string, unknown> };
    "governance.register_minutes": { args: { p_meeting: U; p_registry_protocol: string; p_minutes_doc_code?: string | null }; returns: null };
    "governance.cancel_meeting": { args: { p_meeting: U; p_reason: string }; returns: null };
    "governance.upsert_obligation": {
      args: { p_entity: U; p_title: string; p_kind: string; p_due_on: string; p_recurrence?: string; p_alert_days?: number; p_legal_ref?: string | null; p_responsible_person?: U | null; p_responsible_label?: string | null; p_notes?: string | null; p_id?: U | null };
      returns: U;
    };
    "governance.complete_obligation": { args: { p_obligation: U; p_evidence: string; p_done_on?: string | null }; returns: Record<string, unknown> };
    "governance.waive_obligation": { args: { p_obligation: U; p_reason: string }; returns: U };
  }
}

export function useEntities(orgId: string) {
  return useQuery({ queryKey: ["governance", "entities", orgId], queryFn: () => unwrap<Entity[]>(from("governance", "entities").select("*").eq("org_id", orgId).order("name")) });
}

export function usePartners(entityId: string | undefined) {
  return useQuery({
    queryKey: ["governance", "partners", entityId],
    enabled: Boolean(entityId),
    queryFn: () => unwrap<Partner[]>(from("governance", "partners").select("*").eq("entity_id", entityId!).order("status").order("name")),
  });
}

export function useCapTable(entityId: string | undefined) {
  return useQuery({
    queryKey: ["governance", "cap", entityId],
    enabled: Boolean(entityId),
    queryFn: () => unwrap<CapRow[]>(from("governance", "v_cap_table").select("*").eq("entity_id", entityId!).order("basis_points", { ascending: false })),
  });
}
